import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabase';
import BottomNav from '../components/BottomNav';

interface TopProduct {
  product_id: string;
  name: string;
  quantity: number;
  total: number;
}

interface StatsToday {
  total_sales: number;
  order_count: number;
  avg_ticket: number;
  tables_served: number;
  top_products: TopProduct[];
}

const EMPTY: StatsToday = {
  total_sales: 0,
  order_count: 0,
  avg_ticket: 0,
  tables_served: 0,
  top_products: [],
};

async function fetchStatsEdge(storeId: string): Promise<StatsToday | null> {
  try {
    const { data, error } = await supabase.functions.invoke('tapnpay-stats-today', {
      body: { store_id: storeId },
    });
    if (error || !data) return null;
    const d = data as Record<string, unknown>;
    return {
      total_sales: Number(d.total_sales ?? 0),
      order_count: Number(d.order_count ?? 0),
      avg_ticket: Number(d.avg_ticket ?? 0),
      tables_served: Number(d.tables_served ?? 0),
      top_products: (d.top_products as TopProduct[] | null) ?? [],
    };
  } catch {
    return null;
  }
}

async function fetchStatsFallback(storeId: string): Promise<StatsToday> {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const startIso = start.toISOString();

  const { data } = await supabase
    .from('orders')
    .select('id, total_amount, table_id, items')
    .eq('store_id', storeId)
    .gte('created_at', startIso);

  const rows = (data ?? []) as Array<{
    id: string;
    total_amount: number | null;
    table_id: string | null;
    items: Array<{ product_id?: string; name?: string; qty?: number; quantity?: number; unit_price?: number }> | null;
  }>;

  const orderCount = rows.length;
  const totalSales = rows.reduce((acc, r) => acc + (r.total_amount ?? 0), 0);
  const tables = new Set(rows.map((r) => r.table_id).filter((t): t is string => Boolean(t)));
  const avgTicket = orderCount > 0 ? totalSales / orderCount : 0;

  const productMap = new Map<string, TopProduct>();
  for (const r of rows) {
    for (const it of r.items ?? []) {
      const key = it.product_id ?? it.name ?? 'unknown';
      const qty = it.qty ?? it.quantity ?? 1;
      const price = it.unit_price ?? 0;
      const existing = productMap.get(key);
      if (existing) {
        existing.quantity += qty;
        existing.total += qty * price;
      } else {
        productMap.set(key, {
          product_id: key,
          name: it.name ?? 'Produit',
          quantity: qty,
          total: qty * price,
        });
      }
    }
  }
  const top = Array.from(productMap.values())
    .sort((a, b) => b.quantity - a.quantity)
    .slice(0, 10);

  return {
    total_sales: totalSales,
    order_count: orderCount,
    avg_ticket: avgTicket,
    tables_served: tables.size,
    top_products: top,
  };
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-surface p-4 flex flex-col gap-1" style={{ border: '0.5px solid #000' }}>
      <span className="text-[10px] uppercase tracking-wider text-on-surface-variant">
        {label}
      </span>
      <span className="text-h2 font-bold tabular-nums text-on-surface-strong">{value}</span>
    </div>
  );
}

export default function StatsPage() {
  const { session } = useAuth();
  const [stats, setStats] = useState<StatsToday>(EMPTY);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    if (!session?.storeId) return;
    setLoading(true);
    const fromEdge = await fetchStatsEdge(session.storeId);
    if (fromEdge) {
      setStats(fromEdge);
    } else {
      setStats(await fetchStatsFallback(session.storeId));
    }
    setLoading(false);
  }, [session?.storeId]);

  useEffect(() => {
    void fetchStats();
  }, [fetchStats]);

  return (
    <div className="min-h-screen bg-background pb-[76px]">
      <header
        className="sticky top-0 z-30 bg-surface flex items-center justify-between px-4 h-12"
        style={{ borderBottom: '0.5px solid #e5e7eb' }}
      >
        <p className="text-sm font-bold uppercase tracking-wider text-on-surface-strong">
          Statistiques du jour
        </p>
        <button
          type="button"
          onClick={() => void fetchStats()}
          disabled={loading}
          className="min-h-[36px] px-2 text-on-surface-variant"
        >
          <span className="material-symbols-outlined text-[20px]">refresh</span>
        </button>
      </header>

      <div className="p-3 grid grid-cols-2 gap-3">
        <Kpi
          label="Ventes totales"
          value={`${stats.total_sales.toLocaleString('fr-MA', { minimumFractionDigits: 2 })} MAD`}
        />
        <Kpi label="Commandes" value={String(stats.order_count)} />
        <Kpi
          label="Ticket moyen"
          value={`${stats.avg_ticket.toLocaleString('fr-MA', { minimumFractionDigits: 2 })} MAD`}
        />
        <Kpi label="Tables servies" value={String(stats.tables_served)} />
      </div>

      <section className="p-3">
        <p className="text-[10px] uppercase tracking-wider text-on-surface-variant mb-2 px-1">
          Top produits
        </p>
        {stats.top_products.length === 0 ? (
          <div
            className="bg-surface p-4 text-center text-sm text-on-surface-variant"
            style={{ border: '0.5px solid #e5e7eb' }}
          >
            Aucune donnée
          </div>
        ) : (
          <div className="bg-surface" style={{ border: '0.5px solid #e5e7eb' }}>
            {stats.top_products.map((p, i) => (
              <div
                key={p.product_id}
                className="flex items-center justify-between px-4 py-3 min-h-[48px]"
                style={{
                  borderBottom:
                    i < stats.top_products.length - 1 ? '0.5px solid #e5e7eb' : undefined,
                }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-[11px] font-bold tabular-nums w-6 text-on-surface-variant">
                    #{i + 1}
                  </span>
                  <span className="text-sm text-on-surface">{p.name}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold tabular-nums text-on-surface-strong">
                    {p.quantity}
                  </p>
                  <p className="text-[10px] text-on-surface-variant tabular-nums">
                    {p.total.toLocaleString('fr-MA', { minimumFractionDigits: 2 })} MAD
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <BottomNav />
    </div>
  );
}
